import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, Star, ArrowLeft, Loader, AlertCircle, BookOpen, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';
import ProfessorRatingItem from '../../components/ProfessorRatingItem'; // Import the new component

/**
 * Professor Details Page component
 * Shows professor information and ratings with report functionality
 */
const ProfessorDetailsPage = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  
  const [professor, setProfessor] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ratingsSummary, setRatingsSummary] = useState(null);
  const [ratingsDisabled, setRatingsDisabled] = useState(false);
  
  // Rating form state (if you have a rating form)
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [newRating, setNewRating] = useState({ rating: 5, review: '' });
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    const fetchProfessorDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch professor details
        const profResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/professors/${id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (!profResponse.ok) {
          throw new Error('Failed to fetch professor details');
        }
        
        const profData = await profResponse.json();
        setProfessor(profData);
        
        // Fetch professor ratings
        const ratingsResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/professors/${id}/ratings`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (ratingsResponse.status === 403) {
          // Check if this is because ratings are disabled for this school
          const errorData = await ratingsResponse.json();
          if (errorData.ratingsDisabled) {
            setRatingsDisabled(true);
            setRatings([]);
            setRatingsSummary(null);
          } else {
            throw new Error(errorData.error || 'Failed to fetch professor ratings');
          }
        } else if (!ratingsResponse.ok) {
          throw new Error('Failed to fetch professor ratings');
        } else {
          const ratingsData = await ratingsResponse.json();
          setRatings(ratingsData.ratings || []);
          setRatingsSummary(ratingsData.summary || null);
          setRatingsDisabled(false);
        }
        
      } catch (error) {
        console.error('Error fetching professor details:', error);
        setError(error.message);
        toast.error('Failed to load professor details');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchProfessorDetails();
    }
  }, [id]);

  // Handle rating submission
  const handleRatingSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmittingRating(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/professors/${id}/ratings`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newRating)
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit rating');
      }
      
      // Refresh ratings
      window.location.reload(); // Simple refresh - you could optimize this
      
      toast.success('Rating submitted successfully!');
      setShowRatingForm(false);
      setNewRating({ rating: 5, review: '' });
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error(error.message || 'Failed to submit rating');
    } finally {
      setSubmittingRating(false);
    }
  };

  // Handle rating edit
  const handleEditRating = (rating) => {
    setNewRating({ rating: rating.rating, review: rating.review || '' });
    setShowRatingForm(true);
  };

  // Handle rating delete
  const handleDeleteRating = async (rating) => {
    if (!window.confirm('Are you sure you want to delete this rating?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/professor-ratings/${rating.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to delete rating');
      }
      
      // Remove rating from state
      setRatings(ratings.filter(r => r.id !== rating.id));
      toast.success('Rating deleted successfully');
    } catch (error) {
      console.error('Error deleting rating:', error);
      toast.error('Failed to delete rating');
    }
  };

  // Handle rating report (callback from ProfessorRatingItem)
  const handleRatingReport = (ratingId) => {
    // Optionally update UI to show rating as reported
    // This is just for UI feedback - the rating will still be visible until moderated
    console.log(`Rating ${ratingId} has been reported`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader className="h-8 w-8 text-red-500 animate-spin mr-2" />
        <span className="text-gray-600 dark:text-gray-400">Loading professor details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Error Loading Professor
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          {error}
        </p>
      </div>
    );
  }

  if (!professor) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Professor Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          The professor you're looking for couldn't be found.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Navigation */}
      <div className="flex items-center mb-6">
        <Link 
          to="/professors" 
          className="inline-flex items-center text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Professors
        </Link>
      </div>

      {/* Professor Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mr-4">
              <User size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {professor.name}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {professor.schoolName}
              </p>
              {professor.email && (
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  {professor.email}
                </p>
              )}
            </div>
          </div>
          
          {/* Rating Summary - only show if ratings are enabled */}
          {!ratingsDisabled && ratingsSummary && ratingsSummary.totalRatings > 0 && (
            <div className="text-right">
              <div className="flex items-center justify-end mb-1">
                <Star className="h-5 w-5 text-yellow-500 fill-current mr-1" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {ratingsSummary.averageRating.toFixed(1)}
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {ratingsSummary.totalRatings} rating{ratingsSummary.totalRatings !== 1 ? 's' : ''}
              </p>
            </div>
          )}
          
          {/* Ratings Disabled Message */}
          {ratingsDisabled && (
            <div className="text-right">
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Professor ratings are not available for this school
                </p>
              </div>
            </div>
          )}
        </div>
        
        {professor.bio && (
          <div className="mt-4">
            <p className="text-gray-700 dark:text-gray-300">{professor.bio}</p>
          </div>
        )}
      </div>

      {/* Teaching Sections */}
      {professor.courseSections && professor.courseSections.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <BookOpen className="mr-2" size={24} />
            Currently Teaching
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {professor.courseSections.map((section) => (
              <div
                key={section.id}
                className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <Link
                  to={`/courses/${section.courseId}/sections/${section.id}`}
                  className="block"
                >
                  <div className="font-semibold text-gray-900 dark:text-white text-base mb-2">
                    {section.courseCode}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {section.courseName}
                  </div>
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-500">
                    <Clock size={12} className="mr-1" />
                    {section.semester} {section.year}
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ratings Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Student Ratings
          </h2>
          
          {/* Add Rating Button (if user hasn't rated yet) */}
          {user && !professor.userRating && (
            <button
              onClick={() => setShowRatingForm(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Add Rating
            </button>
          )}
        </div>

        {/* Rating Form (if shown and ratings enabled) */}
        {!ratingsDisabled && showRatingForm && (
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {professor.userRating ? 'Edit Your Rating' : 'Rate This Professor'}
            </h3>
            <form onSubmit={handleRatingSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rating
                </label>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setNewRating({ ...newRating, rating: star })}
                      className="focus:outline-none"
                    >
                      <Star
                        size={24}
                        className={`${
                          star <= newRating.rating
                            ? 'text-yellow-500 fill-current'
                            : 'text-gray-300 dark:text-gray-600'
                        } hover:text-yellow-400 transition-colors`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="mb-4">
                <label htmlFor="review" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Review (Optional)
                </label>
                <textarea
                  id="review"
                  rows="4"
                  value={newRating.review}
                  onChange={(e) => setNewRating({ ...newRating, review: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="Share your experience with this professor..."
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowRatingForm(false);
                    setNewRating({ rating: 5, review: '' });
                  }}
                  disabled={submittingRating}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingRating}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingRating ? 'Submitting...' : 'Submit Rating'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Ratings List - only show if ratings are enabled */}
        {!ratingsDisabled && (
          ratings.length === 0 ? (
            <div className="text-center py-8">
              <Star className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Ratings Yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Be the first to rate this professor!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {ratings.map((rating) => (
                <ProfessorRatingItem
                  key={rating.id}
                  rating={rating}
                  onEdit={handleEditRating}
                  onDelete={handleDeleteRating}
                  onReport={handleRatingReport}
                />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ProfessorDetailsPage;